import { founderGlance, founderHero, founderJourney } from "./founderExperience";
import { TASK_LABEL } from "./taskCopy";

function requirementLines(plan = {}) {
  return (plan.acceptanceCriteria || [])
    .map((item) => (typeof item === "string" ? item : item?.text || item?.action || ""))
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

/**
 * Questions win over change detection so "what should I change?" is answered, not filed as a note.
 */
export function classifyTaskChatUtterance(text = "") {
  const value = String(text || "").trim();
  if (!value) return "empty";
  const lower = value.toLowerCase();
  const looksLikeQuestion =
    /\?/.test(value) ||
    /^(who|what|why|how|when|where|which|can|could|should|is|are|do|does|did|will|would|am|may)\b/i.test(
      lower
    ) ||
    /\b(what|why|how|who|when|where|which)\b.+\b(is|are|should|do|does|can|will)\b/i.test(lower);
  if (looksLikeQuestion) return "question";

  const looksLikeChange =
    /^(please\s+)?(add|remove|change|update|include|exclude|drop|rename|make)\b/i.test(lower) ||
    /\b(add|remove|change|update|include|exclude)\b.+\b(requirement|requirements|scope|outcome|plan|promise|criteria)\b/i.test(
      lower
    ) ||
    /\b(don't|do not)\b.+\b(include|require|need)\b/i.test(lower);
  return looksLikeChange ? "change" : "question";
}

export function answerTaskChat(
  prompt,
  { projection = {}, plan = null, review = null, repo = null } = {}
) {
  const requestText = plan?.featureRequestText || "";
  const hero = founderHero(projection, requestText, review, repo, plan);
  const journey = founderJourney(projection, review, repo, plan);
  const glance = founderGlance(requestText);
  const requirements = requirementLines(plan);
  const currentStep = journey.find((step) =>
    ["current", "needs_attention"].includes(step.state)
  );
  const nextLabel =
    currentStep && hero.primaryAction?.section === currentStep.section && hero.primaryAction?.label
      ? hero.primaryAction.label
      : currentStep?.label || hero.primaryAction?.label || "Keep moving this Task forward";
  const q = String(prompt || "").toLowerCase().trim();
  const kind = classifyTaskChatUtterance(prompt);
  const asked = String(prompt || "").trim();

  if (kind === "change") {
    return {
      kind: "change",
      text:
        "I'll record that under From conversation on the Task details. It does not rewrite the agreed Task until you send a new version to engineering.",
      section: null,
    };
  }

  if (/list (the )?requirements|what are the requirements|what does this (task )?ask|acceptance criteria/.test(q)) {
    if (!requirements.length) {
      return {
        kind: "answer",
        text: `No requirements are listed yet. Describe what to add, and I'll record it on the ${TASK_LABEL} details.`,
      };
    }
    const listed = requirements
      .slice(0, 12)
      .map((item, index) => `${index + 1}. ${item}`)
      .join("\n");
    const more =
      requirements.length > 12 ? `\n…and ${requirements.length - 12} more on the details panel.` : "";
    return {
      kind: "answer",
      text: `This ${TASK_LABEL} currently has ${requirements.length} requirement${
        requirements.length === 1 ? "" : "s"
      }:\n${listed}${more}`,
    };
  }

  if (/what is (this|the) (task|request)|summar(y|ize)|what are we building|outcome/.test(q)) {
    return {
      kind: "answer",
      text: [
        glance.outcome ? `Outcome: ${glance.outcome}` : null,
        requestText ? `Request: ${requestText}` : `There is no ${TASK_LABEL.toLowerCase()} request yet.`,
        `Status: ${hero.headline}`,
      ]
        .filter(Boolean)
        .join("\n\n"),
    };
  }

  if (/what('s| is) next|happen next|should i do|where do i (go|start)|next step/.test(q)) {
    return {
      kind: "answer",
      text: [
        `Next: ${nextLabel}.`,
        projection.whatHappensNext || hero.explanation,
        currentStep ? `That lives under “${currentStep.label}” in progress.` : null,
      ]
        .filter(Boolean)
        .join(" "),
      section: currentStep?.section || hero.primaryAction?.section || null,
    };
  }

  if (/who (needs to act|is waiting|owns)|waiting on|owner/.test(q)) {
    return {
      kind: "answer",
      text: `Waiting on ${hero.ownerLabel}. ${projection.whatHappensNext || ""}`.trim(),
    };
  }

  if (/branch|pull request|pr\b|delivered work|paste|connect/.test(q)) {
    if (hero.deliveredWork?.known && hero.deliveredWork?.label) {
      return {
        kind: "answer",
        text: `Connected work: ${hero.deliveredWork.label}. Review compares that work with this ${TASK_LABEL}.`,
        section: "review",
      };
    }
    return {
      kind: "answer",
      text: "Paste the pull-request or branch link under Delivered work. Connecting it does not change the agreed Task.",
      section: "delivery",
    };
  }

  if (/why.*(blocked|can't|cannot|stale)|approve|decision/.test(q)) {
    const blockers = (projection.blockers || [])
      .map((item) => (typeof item === "string" ? item : item?.text || item?.detail || ""))
      .filter(Boolean)
      .slice(0, 3);
    return {
      kind: "answer",
      text: [
        hero.explanation,
        blockers.length ? `Still in the way:\n- ${blockers.join("\n- ")}` : null,
        `Next action: ${nextLabel}.`,
      ]
        .filter(Boolean)
        .join("\n\n"),
      section: currentStep?.section || hero.primaryAction?.section || null,
    };
  }

  if (/in scope|out of scope|not included|unrelated/.test(q)) {
    const inScope = (glance.inScope || []).map((item) => `- ${item}`).join("\n");
    const notIncluded = (glance.notIncluded || []).map((item) => `- ${item}`).join("\n");
    return {
      kind: "answer",
      text: [
        inScope ? `In scope:\n${inScope}` : "In scope is still thin — check the request on the details panel.",
        notIncluded ? `Not included:\n${notIncluded}` : null,
      ]
        .filter(Boolean)
        .join("\n\n"),
    };
  }

  if (/progress|how far|done|complete/.test(q)) {
    const done = journey.filter((step) => step.state === "complete").length;
    const lines = journey
      .map((step) => {
        const mark =
          step.state === "complete"
            ? "Done"
            : step.state === "current" || step.state === "needs_attention"
              ? "Now"
              : step.state === "waiting"
                ? "Waiting"
                : "Not yet";
        return `- ${mark}: ${step.label}`;
      })
      .join("\n");
    return {
      kind: "answer",
      text: `${done} of ${journey.length} steps done.\n${lines}`,
    };
  }

  if (/prompt|plan for engineering|engineering handoff|send to engineering/.test(q)) {
    return {
      kind: "answer",
      text: "Prompt and plan for engineering are on the Engineering tab. You can edit and copy them there without changing the agreed Task.",
      section: "engineering",
    };
  }

  if (/^(hi|hello|hey|yo)\b|are you (there|working)|can you hear/.test(q)) {
    return {
      kind: "answer",
      text: `Yes — I can answer from this ${TASK_LABEL}. Try “what should I do next?” or “list the requirements.”`,
    };
  }

  if (/help|what can you|how do i use|what do i (ask|type)/.test(q)) {
    return {
      kind: "answer",
      text: `Ask about the request, the requirements, progress, who is waiting, or what to do next. To change the ${TASK_LABEL}, say something like “Add a requirement for …”.`,
    };
  }

  // Default: grounded status card that quotes the question so the reply is visibly new.
  return {
    kind: "answer",
    text: [
      asked ? `You asked: ${asked}` : null,
      `Status: ${hero.headline}`,
      `Waiting on: ${hero.ownerLabel}`,
      `Next: ${nextLabel}`,
      requirements.length
        ? `This ${TASK_LABEL} has ${requirements.length} requirement${
            requirements.length === 1 ? "" : "s"
          }. Ask me to list them, or ask what to do next.`
        : `Ask what this ${TASK_LABEL} is about, what to do next, or say what to add.`,
    ]
      .filter(Boolean)
      .join("\n"),
    section: currentStep?.section || hero.primaryAction?.section || null,
  };
}
